<template>
  <v-menu v-model="dropdownIsOpen" :close-on-content-click="false" location="bottom center" transition="slide-y-transition" offset="4">
    <template #activator="{ props }">
      <v-btn icon size="36" class="ml-2 mr-3 tour-profile" v-bind="props">
        <user-avatar :user="me" :size="36"></user-avatar>
        <v-tooltip :disabled="dropdownIsOpen" location="bottom" activator="parent">{{ $t('NAV.ACCOUNT') }}</v-tooltip>
      </v-btn>

      <!--  MODALS -->
      <premium-window v-if="premiumWindowIsOpened" @close="closeModals"></premium-window>
    </template>
    <v-list class="py-0">
      <v-list-item lines="two">
        <template #prepend>
          <router-link to="/manage#profile" class="mr-3" @click="dropdownIsOpen = false">
            <user-avatar :user="me" :title="$t('ACCOUNT.CHANGE_AVATAR')" :size="48"></user-avatar>
          </router-link>
        </template>
        <div style="position: relative">
          <div class="font-weight-bold">{{ me.firstname || '' }} {{ me.lastname || '' }}</div>
          <div :title="me.email" class="text-medium-emphasis small text-ellipsis" style="position: absolute; width: 100%">{{ me.email }}</div>
          <div class="small">&nbsp;</div> <!-- email placeholder -->
        </div>
      </v-list-item>
      <v-divider></v-divider>
      <v-list-item :title="$t('NAV.MANAGE')" to="/manage" :active="false" lines="two" @click="dropdownIsOpen = false">
        <template #subtitle>
          <span v-if="isTrial" class="text-uppercase">{{ $t('ACCOUNT.FREE_TRIAL') }}</span>
          <span v-else-if="isPremium">{{ me.company && me.company.name }}</span>
          <span v-else>FREE</span>
        </template>
        <template #append>
          <v-btn icon :title="$t('PREMIUM.UPDATE_SUBSCRIPTION')" size="small" class="ml-1" @click.prevent="openPremium()">
            <v-icon size="large" color="accent">fas fa-cart-plus</v-icon>
          </v-btn>
        </template>
      </v-list-item>
      <v-divider v-if="! isPremium"></v-divider>
      <v-list-item v-if="! isPremium" @click="openPremium()">
        <b>{{ $filters.uppercase($t('ACCOUNT.UPGRADE')) }}</b>
      </v-list-item>
      <v-divider></v-divider>
      <v-list-item lines="two">
        <template #prepend>
          <span class="nobr mr-2">{{ $t('ACCOUNT.LANGUAGE') }} :</span>
        </template>
        <v-select v-model="lang" :items="[{ value: 'en', title: 'English' }, { value: 'fr', title: 'Français' }]"
                  density="compact">
        </v-select>
      </v-list-item>
      <v-divider></v-divider>
      <v-list-item>
        <btn-toggle-filter v-model="darkMode" mandatory="force" variant="outlined" density="compact" @click="dropdownIsOpen = false">
          <v-btn :value="false" :title="`${$t('APPEARANCE.THEME')} ${$t('APPEARANCE.THEME_LIGHT')}`">
            <i class="far fa-sun"></i>
          </v-btn>
          <v-btn :value="true" :title="`${$t('APPEARANCE.THEME')} ${$t('APPEARANCE.THEME_DARK')}`">
            <i class="far fa-moon"></i>
          </v-btn>
        </btn-toggle-filter>
        <template #append>
          <v-btn-icon to="manage#profile-appearance" :title="$t('APPEARANCE.APPEARANCE')" :active="false" size="small" @click="dropdownIsOpen = false">
            <v-icon>far fa-palette</v-icon>
          </v-btn-icon>
        </template>
      </v-list-item>
      <v-divider></v-divider>
      <v-list-item @click="dropdownIsOpen = false; toggleFullScreenMode()">
        <i class="far fa-expand mr-2"></i>{{ $t('NAV.FULLSCREEN') }}
      </v-list-item>
      <v-divider></v-divider>
      <v-list-item href="./meeting" target="_blank" rel="noopener" class="text-meetingprimary">
        <i class="far fa-external-link mr-2"></i><span class="nobr">{{ $t('MEETING.BUBBLE_MEETING') }}</span>
      </v-list-item>
      <v-divider></v-divider>
      <v-list-item @click="dropdownIsOpen = false; logout()">
        <i class="far fa-sign-out-alt mr-2"></i>{{ $t('ACCOUNT.LOG_OUT') }}
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
  import useUiNavigationStore from '@/stores/ui/navigation';
  import useUiModalsStore from '@/stores/ui/modals';
  import useLangStore from '@/stores/lang';
  import useUsersStore from '@/stores/users';
  import useUsersAccessRightStore from '@/stores/users/accessRight';
  import darkModeMixin from '@/components/Manage/ManageProfile/darkModeMixin';
  import clientConfig from '@/client_customs/config';
  import PremiumWindow from '@/components/Premium/PremiumWindow.vue';

  export default {
    components: {
      PremiumWindow,
    },
    mixins: [darkModeMixin],
    data() {
      return {
        dropdownIsOpen: null,
        uiNavigationStore: useUiNavigationStore(),
        uiModalsStore: useUiModalsStore(),
        langStore: useLangStore(),
        usersStore: useUsersStore(),
        accessRightStore: useUsersAccessRightStore(),
      };
    },
    computed: {
      lang: {
        get() {
          return this.langStore.lang;
        },
        set(newLang) {
          this.langStore.loadLang(newLang);
        },
      },
      me() {
        return this.usersStore.user;
      },
      premiumWindowIsOpened() {
        return this.uiNavigationStore.openedModal == 'premium-window';
      },
      displayIntegrations() {
        const { availableIntegrations } = this.accessRightStore.config;
        return ! (availableIntegrations && ! availableIntegrations.length);
      },
      isPremium() { return this.accessRightStore.isPremium; },
      isTrial() { return this.accessRightStore.isTrial; },
    },
    methods: {
      openPremium() {
        this.uiNavigationStore.setOpenedModal('premium-window');
      },
      closeModals() {
        this.uiNavigationStore.setOpenedModal(null);
      },
      toggleFullScreenMode() {
        this.uiModalsStore.fullscreenMode = ! this.uiModalsStore.fullscreenMode;
      },
      logout() {
        this.uiNavigationStore.setOpenedModal('logout');
        window.apiSrv.logout().then(() => {
          this.usersStore.reset();
          if (clientConfig.sso) {
            this.uiNavigationStore.setOpenedModal('logoutDone');
          } else {
            window.location.href = "";
          }
        }).catch(() => {
          this.closeModals();
          this.usersStore.reset();
          window.location.href = "";
        });
      },
    },
  };
</script>
